import { useEffect, useRef, useState } from 'react';
import maplibregl, { type Map as MlMap, type GeoJSONSource } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { UploadEvent } from '../sensors';
import { BUILD_TS } from '../build-info';

/**
 * Live control room — map of event locations + SSE ticker + counters.
 *
 *  - On mount: GET /v1/admin/events/recent seeds the map with the last 500
 *    persisted events. The map auto-fits to those bounds, once.
 *    (Endpoint moved from public /v1/events/recent → auth-walled
 *    /v1/admin/events/recent in the #39 aggregation refactor — the
 *    public surface no longer exposes paged raw events.)
 *  - SSE: every newly ingested event appends to the map AND to the
 *    in-tab event list. Heartbeat ticks keep the connection-status pill
 *    fresh.
 *  - Stats poller: /v1/admin/control/stats every 5s for the "since pod boot"
 *    counters.
 */

interface Tick {
  kind: 'tick';
  ts: number;
  seq: number;
}
interface Stats {
  hits: number;
  swerves: number;
  lastEventTs: number;
  podBootTs: number;
  // POST observability — present on backends >= 2026-05-17 15:00.
  // Optional so an older backend still renders the page.
  lastPostTs?: number;
  totalPostsReceived?: number;
  totalEventsAccepted?: number;
  totalEventsRejected?: number;
  recentPostWindow?: {
    windowMs: number;
    batches: number;
    accepted: number;
    rejected: number;
  };
}
type StreamMessage = UploadEvent | Tick;

/** RecentEvent shape from /v1/admin/events/recent. Post-V4: no absolute time. */
interface RecentEvent {
  id: number;
  kind: 'hit' | 'swerve';
  lat: number;
  lon: number;
  speedKmh: number;
  peakG: number | null;
  peakVertSigned: number | null;
  peakLatG: number | null;
  direction: 'left' | 'right' | null;
  confidence: number;
  deviceClass: string;
  eventGuid: string | null;
  tsRelMs: number | null;
}

/**
 * Event classes for the colour + legend:
 *   pothole — hit with peakVertSigned < 0 (wheel dropped first)
 *   bump    — hit with peakVertSigned > 0 (wheel lifted first)
 *   swerve  — lateral spike + return-to-bearing
 *
 * Pre-V3 "hit-legacy" (hit with no peakVertSigned) was removed in V6
 * (2026-05-17): the legacy rows were deleted by migration and the
 * backend now rejects any incoming hit without a signed peak.
 */
type EventClass = 'pothole' | 'bump' | 'swerve';

const CLASS_COLOR: Record<EventClass, string> = {
  pothole: '#b03030', // red
  bump: '#2c8a4a',    // green
  swerve: '#1d6fa6',  // blue
};

const CLASS_LABEL: Record<EventClass, string> = {
  pothole: 'Pothole',
  bump: 'Speed bump',
  swerve: 'Swerve',
};

function classifyHit(peakVertSigned: number | null | undefined): 'pothole' | 'bump' {
  // Post-V6 every persisted hit carries a signed peak. Defensive default
  // for any straggler payload: treat unknown as bump (the less
  // actionable of the two) rather than throw.
  return (peakVertSigned ?? 0) < 0 ? 'pothole' : 'bump';
}

function classifyEvent(e: {
  kind: 'hit' | 'swerve';
  peakVertSigned?: number | null;
}): EventClass {
  return e.kind === 'swerve' ? 'swerve' : classifyHit(e.peakVertSigned);
}

const MAX_EVENTS = 200;
/** Cap on the timeline's in-memory buffer — events older than this drop off
 *  regardless of selected window. 1h is the largest window we offer + a bit. */
const TIMELINE_MAX_AGE_MS = 75 * 60 * 1000;
const TILES_URL = 'https://basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png';
const TILES_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, &copy; <a href="https://carto.com/attributions">CARTO</a>';

/** Compact representation kept in the timeline buffer. */
type TimelineEvent = {
  ts: number; // client-side receive time (Date.now())
  klass: EventClass;
};

const TIMELINE_WINDOWS: { label: string; ms: number }[] = [
  { label: '1m', ms: 60_000 },
  { label: '5m', ms: 5 * 60_000 },
  { label: '15m', ms: 15 * 60_000 },
  { label: '1h', ms: 60 * 60_000 },
];

type MappedEvent = {
  id: string;
  kind: 'hit' | 'swerve';
  klass: EventClass;
  lat: number;
  lon: number;
  /** Magnitude that drives the dot diameter: peakG for hits, peakLatG for swerves. */
  magnitude: number;
  confidence: number;
  deviceClass: string;
  eventGuid: string | null;
  tsRelMs: number | null;
};

function toFeatureCollection(events: MappedEvent[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: events.map((e) => ({
      type: 'Feature',
      id: e.id,
      geometry: { type: 'Point', coordinates: [e.lon, e.lat] },
      properties: {
        klass: e.klass,
        kind: e.kind,
        magnitude: e.magnitude,
        confidence: e.confidence,
        deviceClass: e.deviceClass,
        eventGuid: e.eventGuid,
        tsRelMs: e.tsRelMs,
      },
    })),
  };
}

export function ControlPage() {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MlMap | null>(null);
  const eventsRef = useRef<Map<string, MappedEvent>>(new Map());
  // Monotonic counter for SSE event keys — replaces the old reliance on
  // absolute ts (which the server no longer sends).
  const streamSeqRef = useRef<number>(0);
  const [mapReady, setMapReady] = useState(false);
  const [seeded, setSeeded] = useState(false);

  const [events, setEvents] = useState<UploadEvent[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [connected, setConnected] = useState(false);
  const [lastTickTs, setLastTickTs] = useState<number | null>(null);
  // Timeline buffer — separate from `events` because (a) we only need
  // {ts, klass}, not the full UploadEvent; (b) it lives longer than the
  // 200-event in-tab event list; (c) the timeline ticks every 250 ms
  // even when no new events arrive, so we want a tight render loop
  // that doesn't drag the heavier list around with it.
  const [timelineBuffer, setTimelineBuffer] = useState<TimelineEvent[]>([]);
  const [timelineWindowMs, setTimelineWindowMs] = useState<number>(
    TIMELINE_WINDOWS[1].ms, // default 5 min
  );

  // ── Map init ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapContainerRef.current) return;
    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: {
        version: 8,
        sources: {
          carto: {
            type: 'raster',
            tiles: [TILES_URL],
            tileSize: 256,
            attribution: TILES_ATTRIBUTION,
          },
        },
        layers: [{ id: 'carto', type: 'raster', source: 'carto' }],
      },
      center: [-1.5, 53.5], // default: UK midlands
      zoom: 5.5,
    });
    map.addControl(new maplibregl.NavigationControl(), 'top-right');
    map.addControl(new maplibregl.FullscreenControl(), 'top-right');
    map.on('load', () => {
      map.addSource('events', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'event-circles',
        type: 'circle',
        source: 'events',
        paint: {
          // Colour by event class — see CLASS_COLOR above.
          'circle-color': [
            'match',
            ['get', 'klass'],
            'pothole', CLASS_COLOR.pothole,
            'bump', CLASS_COLOR.bump,
            'swerve', CLASS_COLOR.swerve,
            '#888888',
          ],
          // Radius by event magnitude — bigger jolt, bigger dot.
          // Hits scale by peakG (m/s², ~2..15 range), swerves by peakLatG
          // (~3..8 range). The interpolation tops out at 14px.
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['get', 'magnitude'],
            0, 3,
            3, 5,
            6, 8,
            10, 11,
            15, 14,
          ],
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#ffffff',
          'circle-opacity': 0.85,
        },
      });
      mapRef.current = map;
      setMapReady(true);
    });
    // Click → popup with event detail. HTML rendered via setHTML rather
    // than setDOMContent because maplibre's popup wraps content in its
    // own container; cheaper to ship a single innerHTML write than
    // wire React/DOM into a transient overlay. Values are formatted +
    // string-escaped at the call site so safe to inline.
    map.on('click', 'event-circles', (e) => {
      const f = e.features?.[0];
      if (!f || f.geometry.type !== 'Point') return;
      const [lon, lat] = f.geometry.coordinates as [number, number];
      const p = f.properties as Record<string, unknown>;
      const klass = String(p.klass) as EventClass;
      const title = CLASS_LABEL[klass] ?? String(p.kind);
      const color = CLASS_COLOR[klass] ?? '#888888';
      const magnitude = Number(p.magnitude);
      const confidence = Number(p.confidence);
      const device = escapeHtml(String(p.deviceClass ?? '—'));
      const guidShort = p.eventGuid ? String(p.eventGuid).slice(0, 8) : null;
      const tsRelMs = Number(p.tsRelMs ?? 0);
      const lonLat = `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
      const html = `
        <div class="event-popup event-popup-${klass}">
          <header class="event-popup-header">
            <span class="event-popup-swatch" style="background:${color}"></span>
            <span class="event-popup-title">${escapeHtml(title)}</span>
          </header>
          <dl class="event-popup-grid">
            <dt>Magnitude</dt>
            <dd>${magnitude.toFixed(2)} m/s²</dd>
            <dt>Confidence</dt>
            <dd>${confidence.toFixed(2)}</dd>
            <dt>Device</dt>
            <dd>${device}</dd>
            <dt>Location</dt>
            <dd class="event-popup-mono">${lonLat}</dd>
            ${
              guidShort
                ? `<dt>Window</dt><dd class="event-popup-mono">${guidShort} · +${tsRelMs} ms</dd>`
                : ''
            }
          </dl>
        </div>`;
      new maplibregl.Popup({ closeButton: true, className: 'joltstream-popup' })
        .setLngLat([lon, lat])
        .setHTML(html)
        .addTo(map);
    });
    map.on('mouseenter', 'event-circles', () => {
      map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', 'event-circles', () => {
      map.getCanvas().style.cursor = '';
    });

    return () => {
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
  }, []);

  // ── Seed from /v1/admin/events/recent once ─────────────────────────
  useEffect(() => {
    if (!mapReady || seeded) return;
    fetch('/v1/admin/events/recent?limit=500', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : []))
      .then((recent: RecentEvent[]) => {
        for (const r of recent) {
          const klass = classifyEvent({ kind: r.kind, peakVertSigned: r.peakVertSigned });
          const magnitude = r.kind === 'hit' ? (r.peakG ?? 0) : (r.peakLatG ?? 0);
          eventsRef.current.set(`db:${r.id}`, {
            id: `db:${r.id}`,
            kind: r.kind,
            klass,
            lat: r.lat,
            lon: r.lon,
            magnitude,
            confidence: r.confidence,
            deviceClass: r.deviceClass,
            eventGuid: r.eventGuid,
            tsRelMs: r.tsRelMs,
          });
        }
        applyMap();
        fitToBounds();
        setSeeded(true);
      })
      .catch(() => setSeeded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady]);

  // ── SSE subscription ────────────────────────────────────────────────
  useEffect(() => {
    const es = new EventSource('/v1/admin/control/stream');
    es.onopen = () => setConnected(true);
    es.onerror = () => setConnected(false);
    es.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data) as StreamMessage;
        if (msg.kind === 'tick') {
          setLastTickTs(msg.ts);
        } else {
          setEvents((prev) => [msg, ...prev].slice(0, MAX_EVENTS));
          // Add to map
          const klass = classifyEvent({
            kind: msg.kind,
            peakVertSigned: msg.kind === 'hit' ? msg.peakVertSigned : null,
          });
          // Append to timeline buffer (client-side receive time) and
          // prune anything older than the max window.
          const recvTs = Date.now();
          setTimelineBuffer((prev) => {
            const cutoff = recvTs - TIMELINE_MAX_AGE_MS;
            const next = prev.filter((e) => e.ts >= cutoff);
            next.push({ ts: recvTs, klass });
            return next;
          });
          const magnitude = msg.kind === 'hit' ? msg.peakG : msg.peakLatG;
          // No server-visible absolute time on the new payload; use a
          // local-receive monotonic counter to keep map keys unique.
          const key = `stream:${++streamSeqRef.current}:${msg.lat.toFixed(5)}:${msg.lon.toFixed(5)}`;
          eventsRef.current.set(key, {
            id: key,
            kind: msg.kind,
            klass,
            lat: msg.lat,
            lon: msg.lon,
            magnitude,
            confidence: msg.confidence,
            deviceClass: msg.deviceClass,
            eventGuid: msg.eventGuid ?? null,
            tsRelMs: msg.tsRelMs ?? null,
          });
          applyMap();
        }
      } catch {
        // ignore malformed
      }
    };
    return () => es.close();
  }, []);

  // ── Stats poller ────────────────────────────────────────────────────
  useEffect(() => {
    const fetchStats = () =>
      fetch('/v1/admin/control/stats')
        .then((r) => (r.ok ? r.json() : null))
        .then((s) => s && setStats(s))
        .catch(() => {});
    void fetchStats();
    const id = setInterval(fetchStats, 5000);
    return () => clearInterval(id);
  }, []);

  function applyMap() {
    const map = mapRef.current;
    if (!map) return;
    const src = map.getSource('events') as GeoJSONSource | undefined;
    if (!src) return;
    src.setData(toFeatureCollection(Array.from(eventsRef.current.values())));
  }

  function fitToBounds() {
    const map = mapRef.current;
    if (!map) return;
    const pts = Array.from(eventsRef.current.values());
    if (pts.length === 0) return;
    const bounds = new maplibregl.LngLatBounds();
    for (const p of pts) bounds.extend([p.lon, p.lat]);
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, { padding: 50, maxZoom: 13, animate: false });
    }
  }

  return (
    <div className="page control-page-full">
      <div className="control-header">
        <h1>Control room</h1>
        <div className="control-header-right">
          <BroadcastReloadButton />
          <div className={`pill ${connected ? 'pill-ok' : 'pill-err'}`}>
            {connected ? '● live' : '○ disconnected'}
          </div>
          <div className="build-tag" title="If two browsers show different timestamps, one is on a stale service-worker cache. Reload to refresh.">
            build {BUILD_TS}
          </div>
        </div>
      </div>

      <EventTimeline
        events={timelineBuffer}
        windowMs={timelineWindowMs}
        onChangeWindow={setTimelineWindowMs}
      />

      <div className="control-map-wrap">
        <div className="control-map" ref={mapContainerRef} />
        <details className="map-legend" aria-label="Legend">
          <summary
            className="map-legend-summary"
            aria-label="Event types legend — click to expand"
          >
            {(['pothole', 'bump', 'swerve'] as EventClass[]).map((c) => (
              <span
                key={c}
                className="map-legend-swatch"
                style={{ background: CLASS_COLOR[c] }}
                title={CLASS_LABEL[c]}
                aria-hidden="true"
              />
            ))}
          </summary>
          <div className="map-legend-body">
            <div className="map-legend-title">Event types</div>
            {(['pothole', 'bump', 'swerve'] as EventClass[]).map((c) => (
              <div key={c} className="map-legend-row">
                <span
                  className="map-legend-swatch"
                  style={{ background: CLASS_COLOR[c] }}
                  aria-hidden="true"
                />
                <span>{CLASS_LABEL[c]}</span>
              </div>
            ))}
            <div className="map-legend-size">
              <div className="map-legend-title">Dot size = magnitude</div>
              <div className="map-legend-size-row">
                <span className="dot dot-sm" /> small
                <span className="dot dot-md" /> medium
                <span className="dot dot-lg" /> large
              </div>
            </div>
          </div>
        </details>
      </div>

      <div className="stat-grid">
        <Stat label="Hits since pod boot" value={stats?.hits ?? '—'} />
        <Stat label="Swerves since pod boot" value={stats?.swerves ?? '—'} />
        <Stat
          label="Events on map"
          value={eventsRef.current.size || '—'}
        />
        <Stat
          label="Last heartbeat"
          value={lastTickTs ? timeAgo(lastTickTs) : '—'}
        />
        <Stat
          label="Pod uptime"
          value={stats?.podBootTs ? uptime(stats.podBootTs) : '—'}
        />
        <Stat label="In-tab buffer" value={`${events.length}/${MAX_EVENTS}`} />
        {/* POST observability — tells you instantly whether the silence on
            the map is server-side ("no clients reaching us") or upstream
            ("clients reaching us but events rejected"). */}
        <Stat
          label="POST batches (last 5m)"
          value={
            stats?.recentPostWindow
              ? `${stats.recentPostWindow.batches}`
              : '—'
          }
        />
        <Stat
          label="Events accepted (last 5m)"
          value={
            stats?.recentPostWindow
              ? `${stats.recentPostWindow.accepted}${
                  stats.recentPostWindow.rejected
                    ? ` (+${stats.recentPostWindow.rejected} rejected)`
                    : ''
                }`
              : '—'
          }
        />
        <Stat
          label="Last POST received"
          value={stats?.lastPostTs ? timeAgo(stats.lastPostTs) : '—'}
        />
      </div>

      <h2>Recent events</h2>
      {events.length === 0 ? (
        <p className="hint">
          No new events since this page opened. Stream is{' '}
          {connected ? 'connected' : 'reconnecting…'}. The map above shows everything currently in the database.
        </p>
      ) : (
        <ol className="event-list">
          {events.map((e, i) => {
            const klass = classifyEvent({
              kind: e.kind,
              peakVertSigned: e.kind === 'hit' ? e.peakVertSigned : null,
            });
            const guidShort = e.eventGuid ? e.eventGuid.slice(0, 8) : '—';
            return (
              <li key={`${guidShort}-${i}`} className={`event event-${klass}`}>
                <span
                  className="event-swatch"
                  style={{ background: CLASS_COLOR[klass] }}
                  aria-hidden="true"
                />
                <span className="event-kind">{CLASS_LABEL[klass]}</span>
                <span className="event-coord">
                  {e.lat.toFixed(5)}, {e.lon.toFixed(5)}
                </span>
                <span className="event-meta">
                  {e.kind === 'hit'
                    ? `${e.peakG.toFixed(1)} g · ${e.durationMs} ms`
                    : `${e.peakLatG.toFixed(1)} g lat · Δ${e.headingDeltaDeg.toFixed(0)}° ${e.direction}`}
                </span>
                <span className="event-conf">conf {e.confidence.toFixed(2)}</span>
                <span className="event-device">{e.deviceClass}</span>
                <span className="event-time">
                  {guidShort}
                  {e.tsRelMs != null ? `+${e.tsRelMs}ms` : ''}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

/**
 * Operator button that POSTs to /v1/admin/clients/broadcast-reload —
 * the backend then publishes `{type:"reload"}` to the public
 * /v1/clients/control SSE channel which every connected joltstream
 * tab is subscribed to. Idle tabs reload immediately; tabs that are
 * mid-trip show the UpdateAvailableBanner and reload on Stop.
 *
 * Confirmation prompt is intentional — this kicks people's tabs.
 */
function BroadcastReloadButton() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>(
    'idle',
  );
  const send = async () => {
    if (status === 'sending') return;
    if (!window.confirm(
      'Push a reload to every connected joltstream tab? Recording sessions will defer until Stop.',
    )) return;
    setStatus('sending');
    try {
      const r = await fetch('/v1/admin/clients/broadcast-reload', {
        method: 'POST',
        credentials: 'include',
      });
      setStatus(r.ok ? 'sent' : 'error');
    } catch {
      setStatus('error');
    }
    setTimeout(() => setStatus('idle'), 4000);
  };
  return (
    <button
      type="button"
      className={`broadcast-reload broadcast-reload-${status}`}
      onClick={send}
      disabled={status === 'sending'}
      title="POSTs to /v1/admin/clients/broadcast-reload"
    >
      {status === 'idle' && 'Push reload to clients'}
      {status === 'sending' && 'Pushing…'}
      {status === 'sent' && '✓ Pushed'}
      {status === 'error' && '✕ Failed'}
    </button>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
}

/**
 * Live event timeline strip: horizontal axis = time, right edge = now,
 * left edge = now - windowMs. Each event is a vertical tick coloured by
 * its EventClass. The strip slides as time progresses — re-renders
 * every 250 ms even when no new events arrive.
 *
 * The component owns its own clock (setInterval + useState) so the
 * parent doesn't re-render on every tick.
 */
function EventTimeline({
  events,
  windowMs,
  onChangeWindow,
}: {
  events: TimelineEvent[];
  windowMs: number;
  onChangeWindow: (ms: number) => void;
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(800);
  const [now, setNow] = useState(() => Date.now());

  // Slide the timeline left as time passes. 250 ms gives a perceptibly
  // smooth slide without being expensive — SVG redraw of <300 lines.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, []);

  // Responsive width — ResizeObserver beats a window.resize listener
  // because the parent layout can change without the window doing so.
  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setWidth(Math.max(120, Math.round(e.contentRect.width)));
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  const HEIGHT = 88;
  const AXIS_BAND = 22;
  const PLOT_HEIGHT = HEIGHT - AXIS_BAND;
  const oldest = now - windowMs;
  const xOf = (ts: number) => ((ts - oldest) / windowMs) * width;

  const visible = events.filter((e) => e.ts >= oldest);

  // Five evenly-spaced axis ticks. Right-most is "now"; the rest are
  // labelled as "-Ns / -Nm / -Nh" relative to now.
  const TICK_COUNT = 5;
  const ticks = Array.from({ length: TICK_COUNT }, (_, i) => {
    const fractionFromRight = (TICK_COUNT - 1 - i) / (TICK_COUNT - 1);
    const x = width * (1 - fractionFromRight);
    const ms = fractionFromRight * windowMs;
    return {
      x,
      label: ms < 500 ? 'now' : '-' + formatDuration(ms),
      anchor:
        i === 0 ? ('start' as const) : i === TICK_COUNT - 1 ? ('end' as const) : ('middle' as const),
    };
  });

  return (
    <div className="control-timeline">
      <div className="control-timeline-controls">
        <span className="control-timeline-title">Live timeline</span>
        <div
          className="control-timeline-windows"
          role="radiogroup"
          aria-label="Timeline window"
        >
          {TIMELINE_WINDOWS.map((w) => (
            <button
              key={w.ms}
              role="radio"
              aria-checked={w.ms === windowMs}
              className={w.ms === windowMs ? 'active' : ''}
              onClick={() => onChangeWindow(w.ms)}
              type="button"
            >
              {w.label}
            </button>
          ))}
        </div>
        <span className="control-timeline-count">
          {visible.length} {visible.length === 1 ? 'event' : 'events'} in view
        </span>
      </div>
      <div className="control-timeline-svg-wrap" ref={wrapRef}>
        <svg
          width={width}
          height={HEIGHT}
          aria-label="Event timeline"
          className="control-timeline-svg"
        >
          {/* Plot background */}
          <rect
            x={0}
            y={0}
            width={width}
            height={PLOT_HEIGHT}
            className="timeline-plot-bg"
          />
          {/* Now marker (right edge) */}
          <line
            x1={width - 0.5}
            x2={width - 0.5}
            y1={0}
            y2={PLOT_HEIGHT}
            className="timeline-now"
          />
          {/* Event ticks */}
          {visible.map((e, i) => (
            <line
              key={`${e.ts}-${i}`}
              x1={xOf(e.ts)}
              x2={xOf(e.ts)}
              y1={6}
              y2={PLOT_HEIGHT - 4}
              stroke={CLASS_COLOR[e.klass]}
              strokeWidth={2}
              opacity={0.85}
            />
          ))}
          {/* Axis ticks + labels */}
          {ticks.map((t, i) => (
            <g key={i}>
              <line
                x1={t.x}
                x2={t.x}
                y1={PLOT_HEIGHT}
                y2={PLOT_HEIGHT + 4}
                className="timeline-axis-tick"
              />
              <text
                x={t.x}
                y={HEIGHT - 4}
                textAnchor={t.anchor}
                className="timeline-axis-label"
              >
                {t.label}
              </text>
            </g>
          ))}
        </svg>
        {visible.length === 0 && (
          <div className="control-timeline-empty">
            Waiting for events in this window…
          </div>
        )}
      </div>
    </div>
  );
}

function formatDuration(ms: number): string {
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  if (ms < 60 * 60_000) return `${Math.round(ms / 60_000)}m`;
  return `${Math.round(ms / (60 * 60_000))}h`;
}

/**
 * HTML-escape a string before injecting into setHTML. `deviceClass` is
 * a client-supplied field and reaches the popup unsanitised; explicit
 * escape avoids any cross-site scripting risk if someone POSTs a
 * payload with markup in `deviceClass`.
 */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function timeAgo(ts: number): string {
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  return `${Math.round(s / 3600)}h ago`;
}

function uptime(bootTs: number): string {
  const s = Math.max(0, Math.round((Date.now() - bootTs) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) {
    const h = Math.floor(s / 3600);
    const m = Math.round((s - h * 3600) / 60);
    return `${h}h ${m}m`;
  }
  return `${Math.round(s / 86400)}d`;
}
